import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  PencilLine,
  Sparkle,
  Image,
  Download,
  Trash,
  Spinner
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useLumi, LumiInline } from "./LumiCharacter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const storyPrompts = [
  "Once upon a time, there was a brave little mouse who...",
  "In a magical forest, a curious child found...",
  "A friendly dragon lived in a mountain and one day...",
  "The talking cat had a secret mission to...",
  "When the rainbow appeared, something amazing happened...",
  "In the kingdom of candy, the prince discovered...",
  "The little robot woke up and saw...",
  "Under the sea, a mermaid princess wanted to..."
];

const StoryCreator = ({ player, onBack, onUpdateScore }) => {
  const lumi = useLumi();

  useEffect(() => {
    lumi.setInline(true);
    lumi.wave("Hi! Write a little story and I'll help turn it into a picture.", 3500);
    return () => { lumi.idle(); lumi.setInline(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [storyTitle, setStoryTitle] = useState("");
  const [storyText, setStoryText] = useState("");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedStories, setSavedStories] = useState([]);
  const [showGallery, setShowGallery] = useState(false);

  const fetchStories = async () => {
    try {
      const url = player?.id
        ? `${API}/kids/stories?player_id=${player.id}`
        : `${API}/kids/stories`;
      const response = await axios.get(url);
      setSavedStories(response.data || []);
    } catch (error) {
      console.error("Failed to load stories");
    }
  };

  useEffect(() => {
    fetchStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id]);

  const getRandomPrompt = () => {
    const prompt = storyPrompts[Math.floor(Math.random() * storyPrompts.length)];
    setStoryText(prompt);
  };

  const generateImage = async () => {
    if (storyText.trim().length < 20) {
      toast.error("Please write a longer story (at least 20 characters)!");
      return;
    }

    setIsGenerating(true);
    lumi.read("Painting your story…", 8000);
    try {
      const response = await axios.post(`${API}/kids/story-image`, {
        story: storyText,
        title: storyTitle || "My Story"
      });

      setGeneratedImage(response.data.image_url);

      // Award badge
      if (player && onUpdateScore) {
        if (!player.badges?.includes("story_teller")) {
          onUpdateScore(50, "story_teller");
          toast.success("🏆 You earned the Storyteller badge!");
        } else {
          onUpdateScore(25);
        }
      }

      lumi.celebrate("Look what we made! ✨", 3000);
      toast.success("Your story picture is ready! 🎨");
    } catch (error) {
      console.error("Image generation error:", error);
      lumi.tryAgain("That one was tricky. Let's try once more!", 2500);
      toast.error("Oops! Couldn't make the picture. Try again!");
    }
    setIsGenerating(false);
  };

  const saveStory = async () => {
    if (!storyText || !generatedImage) return;

    try {
      const response = await axios.post(`${API}/kids/stories`, {
        player_id: player?.id || null,
        title: storyTitle || "My Story",
        text: storyText,
        image: generatedImage,
      });
      const newStory = response.data;
      setSavedStories([newStory, ...savedStories].slice(0, 50));
      toast.success("Story saved! 📚");

      // Award creative badge
      if (
        player &&
        onUpdateScore &&
        savedStories.length >= 2 &&
        !player.badges?.includes("creative_genius")
      ) {
        onUpdateScore(50, "creative_genius");
        toast.success("🏆 You earned the Creative Genius badge!");
      }
    } catch (error) {
      console.error("Save story error:", error);
      toast.error("Couldn't save story. Try again!");
    }
  };

  const deleteStory = async (id) => {
    try {
      await axios.delete(`${API}/kids/stories/${id}`);
      setSavedStories(savedStories.filter((s) => s.id !== id));
      toast.success("Story deleted!");
    } catch (error) {
      toast.error("Couldn't delete story");
    }
  };

  const startNewStory = () => {
    setStoryTitle("");
    setStoryText("");
    setGeneratedImage(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          onClick={onBack}
          className="font-nunito border-2 border-foreground rounded-full"
        >
          <ArrowLeft size={20} weight="bold" className="mr-2" />
          Back to Kid's Corner
        </Button>

        <Button
          variant="outline"
          onClick={() => setShowGallery(!showGallery)}
          className="font-nunito border-2 border-foreground rounded-full"
        >
          <Image size={20} weight="bold" className="mr-2" />
          My Stories ({savedStories.length})
        </Button>
      </div>

      {showGallery ? (
        /* Gallery View */
        <div>
          <h1 className="font-fredoka font-bold text-3xl text-foreground mb-6 text-center">
            📚 My Story Gallery
          </h1>

          {savedStories.length === 0 ? (
            <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-12 text-center">
              <p className="font-nunito text-lg text-muted-foreground mb-4">
                No stories yet! Create your first one! ✨
              </p>
              <Button
                onClick={() => setShowGallery(false)}
                className="bg-accent-purple hover:bg-purple-600 text-white font-nunito font-bold border-2 border-foreground shadow-brutal rounded-xl"
              >
                Create Story
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {savedStories.map((story) => (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white border-4 border-foreground rounded-2xl shadow-brutal overflow-hidden"
                >
                  <img 
                    src={story.image} 
                    alt={story.title}
                    className="w-full h-48 object-cover border-b-4 border-foreground"
                  />
                  <div className="p-4">
                    <h3 className="font-fredoka font-bold text-lg text-foreground mb-2">
                      {story.title}
                    </h3>
                    <p className="font-nunito text-sm text-muted-foreground line-clamp-2 mb-3">
                      {story.text}
                    </p>
                    <div className="flex gap-2">
                      <a 
                        href={story.image} 
                        download={`${story.title}.png`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button 
                          size="sm" 
                          className="w-full bg-primary text-white font-nunito border-2 border-foreground rounded-lg"
                        >
                          <Download size={16} className="mr-1" />
                          Save
                        </Button>
                      </a>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => deleteStory(story.id)}
                        className="font-nunito border-2 border-foreground rounded-lg"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Story Creator View */
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-fredoka font-bold text-4xl text-foreground mb-4">
              ✨ Story Creator
            </h1>
            <p className="font-nunito text-lg text-muted-foreground">
              Write a story and turn it into a magical picture!
            </p>
          </div>

          <div className="bg-white border-4 border-foreground rounded-3xl shadow-brutal p-8">
            <LumiInline className="mb-5" />
            {/* Title Input */}
            <div className="mb-6">
              <label className="font-fredoka font-bold text-foreground mb-2 block">
                Story Title
              </label>
              <Input
                type="text"
                placeholder="Give your story a name..."
                value={storyTitle}
                onChange={(e) => setStoryTitle(e.target.value)}
                className="font-nunito text-lg border-4 border-foreground rounded-xl"
                maxLength={50}
              />
            </div>

            {/* Story Text */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="font-fredoka font-bold text-foreground">
                  Your Story
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={getRandomPrompt}
                  className="font-nunito text-sm border-2 border-foreground rounded-full"
                >
                  <Sparkle size={16} className="mr-1" />
                  Get Idea
                </Button>
              </div>
              <Textarea
                placeholder="Once upon a time..."
                value={storyText}
                onChange={(e) => setStoryText(e.target.value)}
                className="font-nunito text-lg border-4 border-foreground rounded-xl min-h-[200px] resize-none"
                maxLength={500}
              />
              <p className="font-nunito text-sm text-muted-foreground mt-2 text-right">
                {storyText.length}/500 characters
              </p>
            </div>

            {/* Generate Button */}
            <Button
              onClick={generateImage}
              disabled={isGenerating || storyText.trim().length < 20}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-fredoka font-bold text-xl border-4 border-foreground shadow-brutal active-brutal rounded-xl py-6"
            >
              {isGenerating ? (
                <span className="flex items-center justify-center">
                  <Spinner size={28} className="animate-spin mr-3" />
                  Creating Magic...
                </span>
              ) : (
                <>
                  <Sparkle size={28} weight="fill" className="mr-3" />
                  Make My Picture!
                </>
              )}
            </Button>

            {storyText.trim().length > 0 && storyText.trim().length < 20 && (
              <p className="font-nunito text-sm text-orange-500 mt-2 text-center">
                Write at least 20 characters to make a picture!
              </p>
            )}
          </div>

          {/* Generated Image */}
          <AnimatePresence>
            {generatedImage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-8 bg-white border-4 border-foreground rounded-3xl shadow-brutal p-8"
              >
                <h2 className="font-fredoka font-bold text-2xl text-foreground mb-4 text-center">
                  🎨 Your Story Picture!
                </h2>
                
                <img
                  src={generatedImage}
                  alt="Generated story illustration"
                  className="w-full rounded-2xl border-4 border-foreground mb-6"
                />

                <div className="flex flex-wrap gap-3 justify-center">
                  <a 
                    href={generatedImage} 
                    download={`${storyTitle || "my-story"}.png`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal rounded-xl">
                      <Download size={20} className="mr-2" />
                      Download Picture
                    </Button>
                  </a>
                  
                  <Button
                    onClick={saveStory}
                    className="bg-green-500 hover:bg-green-600 text-white font-nunito font-bold border-2 border-foreground shadow-brutal rounded-xl"
                  >
                    <Image size={20} className="mr-2" />
                    Save to Gallery
                  </Button>

                  <Button
                    variant="outline"
                    onClick={startNewStory}
                    className="font-nunito font-bold border-2 border-foreground rounded-xl"
                  >
                    <PencilLine size={20} className="mr-2" />
                    New Story
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default StoryCreator;
